import React, {useEffect, useCallback, useMemo, useState} from "react";
import {Route} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import styled from 'styled-components';
import { ScalePrintTabs } from "./ScalePrintTabs";
import { useApi } from "../../lib/api";
import useAsync from "../../lib/use-async";
import Button from "../../components/Button";
import ListScreen from "../../components/ListScreen";
import ListItem from "../../components/ListItem";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import SideModal from "../../components/SideModal";
import Form from "../../components/Form";
import Input from "../../components/Input";
import { useUrlState } from "../../lib/with-url-state";
import search from "../../lib/search";
import { alphaSort } from "../../lib/sort";
import { T } from "../../components/I18n";

const DEFAULT_PORT = 9100; // default port for printers

export function PrinterConfigurationsTab() {
  const api = useApi();
  const [{filter, sort}, setUrlState] = useUrlState({filter: '', sort: 'asc'});
  const [getPrinterConfigurations, {loading, error, result}] = useAsync(api.getPrinterConfigurations);
  const [getScaleConfigurations, {result: scaleConfigsResult}] = useAsync(api.getScaleConfigurations);
  const [printerConfigurations, setPrinterConfigurations] = useState([]);
  const [scaleConfigurations, setScaleConfigurations] = useState([]);

  useEffect(() => {
    getPrinterConfigurations();
    getScaleConfigurations();
  }, []);

  useEffect(() => {
    setPrinterConfigurations(result || []);
  }, [result]);

  useEffect(() => {
    setScaleConfigurations(scaleConfigsResult || []);
  }, [scaleConfigsResult]);

  const scaleConfigsById = useMemo(() => {
    const map = {};
    for (const sc of scaleConfigurations) {
      map[sc.id] = sc;
    }
    return map;
  }, [scaleConfigurations]);

  const handleSearch = useCallback((e) => {
    setUrlState({filter: e.target.value});
  }, [setUrlState]);

  const handleSortChange = useCallback((e) => {
    setUrlState({sort: e.target.value});
  }, [setUrlState]);

  const handleSave = useCallback((printerConfiguration) => {
    setPrinterConfigurations(prev => prev.filter(p => p.id !== printerConfiguration.id).concat([printerConfiguration]));
  }, []);

  const handleDelete = useCallback(async (printerConfiguration) => {
    if (!window.confirm(`Are you sure you want to delete the printer at "${printerConfiguration.host}:${printerConfiguration.port}"?`)) {
      return;
    }

    try {
      await api.deletePrinterConfiguration(printerConfiguration.id);
      setPrinterConfigurations(prev => prev.filter(p => p.id !== printerConfiguration.id));
      NotificationManager.success(<>Printer at <T noTranslate>{printerConfiguration.host}</T> was deleted.</>);
    }
    catch (e) {
      NotificationManager.error('Failed to delete printer configuration. Please try again.');
    }
  }, []);

  const filteredAndSortedPrinters = printerConfigurations
    .filter(config => {
      const scale = scaleConfigsById[config.scaleConfigurationId];
      const haystack = `${config.host} ${config.port} ${scale ? scale.serialNumber : ''}`;
      return search(haystack, filter);
    })
    .sort(alphaSort(sort, config => config.host || ''));

  return (
    <>
      <ListScreen
        title="Devices"
        activeLink="Devices"
        tabs={<ScalePrintTabs />}
        placeholder="Search printers..."
        search={filter}
        onSearch={handleSearch}
        loading={loading && <Loading fullScreen />}
        error={error && <Error fullScreen />}
        primaryAction={<Button to="/devices/printer-configurations/create">Create Printer Configuration</Button>}
        items={filteredAndSortedPrinters}
        filter={filter}
        toolbarActions={[
          <Input id="printer-sort" label="Sort by:" type="select" value={sort} onChange={handleSortChange}>
            <option value="asc">Alphabetical</option>
            <option value="desc">Reverse Alphabetical</option>
          </Input>
        ]}
        empty={
          printerConfigurations.length < 1 ? (
            <p>No printer configurations have been created yet. Click <b>Create Printer Configuration</b> to get started.</p>
          ) : (
            <p>No printer configurations found.</p>
          )
        }
      >
        {(config, i) => {
          const scale = scaleConfigsById[config.scaleConfigurationId];
          return (
            <ListItem
              first={i === 0}
              options={[
                {title: 'Edit', to: `/devices/printer-configurations/${config.id}`},
                {title: 'Delete', onClick: () => handleDelete(config)}
              ]}
            >
              <PrinterRow>
                <HostLine>
                  <T noTranslate>{config.host}</T>:<T noTranslate>{String(config.port)}</T>
                </HostLine>
                <Details>
                  {scale ? (
                    <>Scale: <T noTranslate>{scale.serialNumber}</T></>
                  ) : (
                    <Muted>No scale configuration linked</Muted>
                  )}
                </Details>
              </PrinterRow>
            </ListItem>
          );
        }}
      </ListScreen>
      <Route
        path="/devices/printer-configurations/:id?"
        render={(props) => (
          <ManagePrinterConfigurationModal
            key={props.match.params.id}
            {...props}
            scaleConfigurations={scaleConfigurations}
            printerConfigurations={printerConfigurations}
            onSave={handleSave}
          />
        )}
      />
    </>
  );
}

function ManagePrinterConfigurationModal({history, match: {params: {id}}, scaleConfigurations, printerConfigurations, onSave}) {
  const isNew = id === 'create';
  const api = useApi();
  const [getPrinterConfiguration, {loading, error, result}] = useAsync(api.getPrinterConfiguration);
  const [printerConfiguration, setPrinterConfiguration] = useState(createBlank());
  const [saving, setSaving] = useState(false);
  const [validationEnabled, setValidationEnabled] = useState(!isNew);

  useEffect(() => {
    if (id && !isNew) {
      getPrinterConfiguration(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    setPrinterConfiguration(result || createBlank());
  }, [result]);

  const set = useCallback((property, value) => {
    setPrinterConfiguration(prev => ({...prev, [property]: value}));
  }, []);

  const handleClose = useCallback(() => {
    history.push('/devices/printer-configurations');
  }, [history]);

  const usedScaleIds = useMemo(() => {
    const set = new Set();
    for (const p of printerConfigurations) {
      if (p.scaleConfigurationId && p.id !== printerConfiguration.id) {
        set.add(p.scaleConfigurationId);
      }
    }
    return set;
  }, [printerConfigurations, printerConfiguration.id]);

  const availableScaleConfigurations = scaleConfigurations.filter(sc =>
    !usedScaleIds.has(sc.id) || sc.id === printerConfiguration.scaleConfigurationId
  );

  const errors = {};
  if (!printerConfiguration.scaleConfigurationId) {
    errors.scaleConfigurationId = 'A scale configuration is required';
  }
  if (!printerConfiguration.host || !printerConfiguration.host.trim()) {
    errors.host = 'A host is required';
  }
  const portValue = Number(printerConfiguration.port);
  if (!Number.isInteger(portValue) || portValue < 1 || portValue > 65535) {
    errors.port = 'Port must be a number between 1 and 65535';
  }

  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = useCallback(async () => {
    setValidationEnabled(true);

    if (hasErrors) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...printerConfiguration,
        host: printerConfiguration.host.trim(),
        port: Number(printerConfiguration.port)
      };
      const saved = isNew
        ? await api.createPrinterConfiguration(payload)
        : await api.updatePrinterConfiguration(payload);

      NotificationManager.success(<>Printer at <T noTranslate>{saved.host}</T> was saved.</>);
      history.push('/devices/printer-configurations');
      onSave(saved);
    }
    catch (e) {
      NotificationManager.error('Unable to save printer configuration. Please try again.');
    }
    finally {
      setSaving(false);
    }
  }, [printerConfiguration, hasErrors, isNew, onSave, history]);

  return (
    <Form onSubmit={handleSubmit}>
      <SideModal
        open={typeof id !== "undefined"}
        title={`${isNew ? 'Create' : 'Edit'} Printer Config`}
        onClose={saving ? () => {} : handleClose}
        options={[
          <Button key="save" type="submit" disabled={saving || (validationEnabled && hasErrors)}>
            {saving ? 'Saving...' : 'Save Printer Configuration'}
          </Button>,
          <Button key="cancel" secondary onClick={handleClose} disabled={saving}>Cancel</Button>
        ]}
      >
        {loading ? (
          <Loading fullScreen />
        ) : error ? (
          <Error fullScreen />
        ) : (
          <SideModal.Content>
            <Form.Group>
              <Input
                id="scale-configuration"
                type="select"
                label="Scale Configuration"
                value={printerConfiguration.scaleConfigurationId || ''}
                onChange={e => set('scaleConfigurationId', e.target.value)}
                invalid={validationEnabled && !!errors.scaleConfigurationId}
                validationText={validationEnabled ? errors.scaleConfigurationId : ''}
              >
                <option value="" disabled>Select a scale configuration</option>
                {availableScaleConfigurations.length === 0 ? (
                  <option value="" disabled>No unassigned scale configurations available</option>
                ) : (
                  availableScaleConfigurations
                    .sort((a, b) => (a.serialNumber || '').localeCompare(b.serialNumber || ''))
                    .map(sc => (
                      <option key={sc.id} value={sc.id}>{sc.serialNumber}</option>
                    ))
                )}
              </Input>
            </Form.Group>
            <Form.Group>
              <Input
                id="host"
                type="text"
                label="Host"
                placeholder="e.g. 192.168.1.50"
                value={printerConfiguration.host || ''}
                onChange={e => set('host', e.target.value)}
                invalid={validationEnabled && !!errors.host}
                validationText={validationEnabled ? errors.host : ''}
              />
            </Form.Group>
            <Form.Group>
              <Input
                id="port"
                type="number"
                label="Port"
                min={1}
                max={65535}
                value={printerConfiguration.port}
                onChange={e => set('port', e.target.value)}
                invalid={validationEnabled && !!errors.port}
                validationText={validationEnabled ? errors.port : ''}
              />
            </Form.Group>
          </SideModal.Content>
        )}
      </SideModal>
    </Form>
  );
}

// creates with the default port of 9100
function createBlank() {
  return {
    scaleConfigurationId: '',
    host: '',
    port: DEFAULT_PORT
  };
}

const PrinterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HostLine = styled.strong`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Details = styled.small`
  display: block;
  color: #747474;
`;

const Muted = styled.span`
  color: #a0a0a0;
  font-style: italic;
`;